const { viewportSize } = window.BAO.Utils

const STATIC_DOTS_MAX = 5
const TONE_SAMPLE_BAND = 0.15
const TONE_LUMINANCE_THRESHOLD = 0.6

class ProductMediaCarousel extends window.BAO.CustomElement() {
  static get requiredElements () {
    return ['slides', 'slide']
  }

  connectedCallback () {
    super.connectedCallback()
    if (this.els?.slide?.elements?.length < 2) return

    this.initialiseCarousel()
  }

  setupListeners () {
    super.setupListeners()

    this.handleViewportChange = this.handleViewportChange.bind(this)
    this.requestTrackUpdate = this.requestTrackUpdate.bind(this)
    this.updateTrackPlacement = this.updateTrackPlacement.bind(this)

    this.mobileMediaQuery = window.matchMedia('(max-width: 767px)')
    this.listeners.add(
      this.mobileMediaQuery,
      'change',
      this.handleViewportChange,
    )
  }

  handleViewportChange (event) {
    if (!event.matches) return

    this.initialiseCarousel()
  }

  initialiseCarousel () {
    if (viewportSize() !== 'xs' || this.carousel) return

    const { track, counter, slide } = this.els

    this.trackEl = track?.element
    this.counterEl = counter?.element
    this.slideEls = slide?.elements ?? []
    this.tones = new Map()

    this.carousel = this.setupCarousel()

    this.listeners.add(window, 'scroll', this.requestTrackUpdate)
    this.listeners.add(window, 'resize', this.requestTrackUpdate)
    this.listeners.add(window, 'load', this.requestTrackUpdate)

    this.trackResizeObserver = new ResizeObserver(this.requestTrackUpdate)
    this.trackResizeObserver.observe(this)

    this.updateTrackTone()
  }

  disconnectedCallback () {
    super.disconnectedCallback()
    if (this.trackResizeObserver) this.trackResizeObserver.disconnect()
  }

  updateCounter () {
    if (!this.counterEl || !this.carousel) return

    this.counterEl.textContent = String(this.carousel.activeIndex + 1)
  }

  activeSlide () {
    return this.slideEls?.[this.carousel?.activeIndex]
  }

  activeMedia () {
    return this.activeSlide()?.querySelector('.rsp-Image_Image, video')
  }

  requestTrackUpdate () {
    if (!this.isTicking) window.requestAnimationFrame(this.updateTrackPlacement)

    this.isTicking = true
  }

  updateTrackPlacement () {
    this.isTicking = false
    if (!this.trackEl || !this.mobileMediaQuery.matches) return

    const mediaRect = this.activeMedia()?.getBoundingClientRect()
    const trackRect = this.trackEl.getBoundingClientRect()
    const trackMiddle = trackRect.top + trackRect.height / 2
    const overMedia =
      mediaRect != null &&
      trackMiddle >= mediaRect.top &&
      trackMiddle <= mediaRect.bottom

    this.trackEl.classList.toggle('prd-Media_Track-detached', !overMedia)
  }

  updateTrackTone () {
    const slide = this.activeSlide()
    if (!this.trackEl || !slide) return

    this.resolveTone(slide).then(tone => {
      if (this.activeSlide() !== slide) return

      this.trackEl.classList.toggle('prd-Media_Track-dark', tone === 'dark')

      slide
        .querySelector('.prd-Media_Button')
        ?.classList.toggle('prd-Media_Button-light', tone === 'light')
    })
  }

  async resolveTone (slide) {
    const { trackTone } = slide.dataset
    if (trackTone) return trackTone

    const source = this.toneSource(slide)
    if (!source) return 'light'

    if (!this.tones.has(source)) this.tones.set(source, this.sampleTone(source))

    return this.tones.get(source)
  }

  toneSource (slide) {
    const video = slide.querySelector('video')
    const src =
      slide.querySelector('.rsp-Image_Image')?.getAttribute('src') ||
      video?.dataset?.poster ||
      video?.poster
    if (!src) return null

    return src.replace(/([?&]width=)\d+/, '$1100')
  }

  async sampleTone (source) {
    const image = await this.loadedImage(source)
    const width = image?.naturalWidth
    const height = image?.naturalHeight
    if (!width || !height) return 'light'

    const canvas = document.createElement('canvas')
    canvas.width = 1
    canvas.height = 1

    // willReadFrequently pins the CPU backend — the GPU one filters the
    // downscale differently and flips borderline images across the threshold
    const context = canvas.getContext('2d', { willReadFrequently: true })
    const band = Math.max(1, Math.round(height * TONE_SAMPLE_BAND))

    try {
      context.drawImage(image, 0, height - band, width, band, 0, 0, 1, 1)

      const [red, green, blue] = context.getImageData(0, 0, 1, 1).data
      const luminance = (0.2126 * red + 0.7152 * green + 0.0722 * blue) / 255

      return luminance > TONE_LUMINANCE_THRESHOLD ? 'dark' : 'light'
    } catch (error) {
      return 'light'
    }
  }

  loadedImage (source) {
    return new Promise(resolve => {
      const image = new Image()

      image.addEventListener('load', () => resolve(image), { once: true })
      image.addEventListener('error', () => resolve(null), { once: true })

      image.src = source
    })
  }

  setupCarousel () {
    const slideCount = this.slideEls.length

    // eslint-disable-next-line no-undef
    const swiper = new Swiper(this.els.slides.element, {
      zoom: {
        maxRatio: 5,
        minRatio: 1,
        toggle: true,
        containerClass: 'swiper-zoom-container',
        zoomedSlideClass: 'swiper-slide-zoomed',
        scale: 1,
        doubleTap: true,
        doubleTapToggle: true,
        pinchToClose: true,
        zoomToSlide: true,
        scaleRatio: 0.5,
        minScale: 1,
        maxScale: 5,
      },
      loop: false,
      pagination: {
        el: this.els?.dots?.element,
        clickable: true,
        dynamicBullets: slideCount > STATIC_DOTS_MAX,
        dynamicMainBullets: 3,
        bulletClass: 'prd-Media_Dot',
        bulletActiveClass: 'prd-Media_Dot-active',
        clickableClass: 'prd-Media_Dots-clickable',
        horizontalClass: 'prd-Media_Dots-horizontal',
        lockClass: 'prd-Media_Dots-lock',
        modifierClass: 'prd-Media_Dots-',
      },
      on: {
        slideChange: () => {
          this.updateCounter()
          this.updateTrackTone()
          this.requestTrackUpdate()
        },
      },
      touchRatio: 1,
      touchAngle: 45,
      touchMoveStopPropagation: true,
      preventInteractionOnTransition: true,
      touchStartPreventDefault: false,
      touchStartForcePreventDefault: false,
      touchMovePreventDefault: false,
      touchEndPreventDefault: false,
      allowTouchMove: true,
      followFinger: true,
      threshold: 5,
      breakpoints: {
        768: {
          allowTouchMove: false,
          touchRatio: 0,
          zoom: {
            enabled: false,
          },
        },
      },
    })

    if (viewportSize() === 'xs') {
      // Add pointerdown event listener to handle multi-touch
      const zoomContainers = this.querySelectorAll('.swiper-zoom-container')

      zoomContainers.forEach(zoomContainer => {
        let fingerCount = 0

        // Prevent default touch events that might interfere with pinch
        zoomContainer.addEventListener(
          'touchstart',
          e => {
            if (e.touches.length > 1) {
              e.preventDefault()
            }
          },
          { passive: false },
        )

        zoomContainer.addEventListener(
          'touchmove',
          e => {
            if (e.touches.length > 1) {
              e.preventDefault()
            }
          },
          { passive: false },
        )

        // Handle pointer events for more precise control
        zoomContainer.addEventListener('pointerdown', e => {
          fingerCount++
          if (fingerCount === 2) {
            zoomContainer.style.touchAction = 'none'
            // Also prevent default to ensure no browser gestures interfere
            e.preventDefault()
          } else {
            // Allow scrolling with one finger, even when zoomed
            zoomContainer.style.touchAction = 'pan-x pan-y pinch-zoom'
          }
        })

        zoomContainer.addEventListener('pointermove', e => {
          if (fingerCount === 2) {
            e.preventDefault()
          }
        })

        zoomContainer.addEventListener('pointerup', () => {
          fingerCount--
          if (fingerCount < 2) {
            // Always allow scrolling with one finger
            zoomContainer.style.touchAction = 'pan-x pan-y pinch-zoom'
          }
        })

        zoomContainer.addEventListener('pointercancel', () => {
          fingerCount = 0
          // Always allow scrolling with one finger
          zoomContainer.style.touchAction = 'pan-x pan-y pinch-zoom'
        })
      })
    }

    return swiper
  }
}

if (!customElements.get('product-media-carousel')) {
  customElements.define('product-media-carousel', ProductMediaCarousel)
}
